import React from 'react'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { IconButton, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import FlexRow from '../../../../components/molecules/FlexRow'
import CurrentUserAvatar from '../../../../components/atoms/UserAvatar/CurrentUserAvatar'
import Icon from '../../../../components/atoms/Icon'
import DenaliLogo from '../common/DenaliLogo'
import { useFormattingContext } from '../../../../components/organisms/FormattingProvider/FormattingContext'

const useStyles = makeStyles(() => ({
  avatar: {
    width: '20px',
    height: '20px',
    borderRadius: 'var(--border-radius-round)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 'var(--font-size-sm)',
    backgroundColor: 'var(--dai-color-surface1)',
    color: 'var(--dai-color-text)'
  },
  dai: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 'var(--font-size-sm)'
  },
  generatingStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--color-info-500)',
    fontStyle: 'italic'
  },
  messageHeader: {
    borderBottom: 'var(--border-neutral-thin)'
  }
}))

function MessageHeader ({ message, showExplainability, onExplainClick }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const isUser = message.actor === 'user'
  const hasEvents = !isUser

  // Format timestamp: show date if yesterday or before, otherwise just time
  const formatTimestamp = (createdDate) => {
    if (!createdDate) return ''
    const messageDate = dayjs(createdDate)
    const today = dayjs().startOf('day')
    const messageDay = messageDate.startOf('day')

    // If message is from today, show only time
    if (messageDay.isSame(today)) {
      return formatter(createdDate, 'timeAbsolute')
    }
    // If message is from yesterday or before, show date and time
    return formatter(createdDate, 'localDateTime')
  }

  if (isUser) {
    return (
      <FlexRow className={clsx('justify-between align-center pb-2 mb-2', classes.messageHeader)}>
        <FlexRow className='gap-sm'>
          <div className={classes.avatar}>
            <CurrentUserAvatar style={{ width: '20px' }} className='border-sm' size='xs' />
          </div>
          <span className='font-sans font-size-xs font-bold'>
            You
          </span>
          {!message.isPlaceholder ? (
            <span className='font-sans font-size-xs' style={{ color: 'var(--dai-color-text-low)' }}>
              {formatTimestamp(message.createdDate)}
            </span>
          ) : null}
        </FlexRow>
      </FlexRow>
    )
  }

  return (
    <FlexRow className={clsx('justify-between align-center pb-2 mb-2', classes.messageHeader)}>
      <FlexRow className='gap-sm items-center'>
        <div className={classes.dai}>
          <DenaliLogo size='36px' />
        </div>
        <span className='font-sans font-size-xs font-bold'>
          Denali AI
        </span>
        {!message.isPlaceholder ? (
          <span className='font-sans font-size-xs' style={{ color: 'var(--dai-color-text-low)' }}>
            {formatTimestamp(message.createdDate)}
          </span>
        ) : null}
      </FlexRow>
      {/* Toggle button for explainability - only show for assistant messages with events */}
      {hasEvents ? (
        <FlexRow>
          <IconButton
            size='small'
            onClick={onExplainClick}
            title={showExplainability ? 'Hide AI process' : 'Show AI process'}
          >
            <Icon
              name='visibility'
              customSize='16px'
              style={{ color: showExplainability ? 'var(--color-info-500)' : 'var(--color-neutral-400)' }}
            />
          </IconButton>
        </FlexRow>
      ) : null}
    </FlexRow>
  )
}

MessageHeader.propTypes = {
  message: PropTypes.shape({
    isPlaceholder: PropTypes.bool,
    actor: PropTypes.string,
    isGenerating: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    generationStatus: PropTypes.string,
    createdDate: PropTypes.string,
    data: PropTypes.object
  }),
  showExplainability: PropTypes.bool,
  onExplainClick: PropTypes.func
}

export default MessageHeader
